import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  API_BASE,
  deletePageAsset,
  editPage,
  generatePageAsset,
  getLivePageUrl,
  getPageDetails,
  listPageAssets,
  translatePage,
  updatePageBrand,
  uploadPageAsset,
} from '../services/api';
import { useT } from '../i18n/LangContext';
import AppNav from '../components/AppNav';

function ToolIconMedia() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M21 15v2.5A2.5 2.5 0 0 1 18.5 20h-13A2.5 2.5 0 0 1 3 17.5v-11A2.5 2.5 0 0 1 5.5 4h7.4c.3 0 .6.1.8.3l2.6 2.6c.2.2.3.5.3.8V9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 12.5 10.8 9.8c.4-.4 1.1-.4 1.5 0l4.7 4.7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m15.2 13.5 1.3-1.3c.4-.4 1.1-.4 1.5 0L21 15" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ToolIconColors() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 4a8 8 0 1 0 8 8c0-1.2-.9-2-2-2h-1.2c-.7 0-1.3-.6-1.3-1.3V7.5A3.5 3.5 0 0 0 12 4Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <circle cx="8.1" cy="10.1" r="1" fill="currentColor" />
      <circle cx="10.9" cy="7.8" r="1" fill="currentColor" />
      <circle cx="15.3" cy="8.4" r="1" fill="currentColor" />
      <circle cx="16.5" cy="12.3" r="1" fill="currentColor" />
    </svg>
  );
}

function ToolIconTemplates() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="3" width="8" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.7" />
      <rect x="13" y="3" width="8" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.7" />
      <rect x="3" y="10" width="8" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.7" />
      <rect x="13" y="10" width="8" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function ToolIconBackground() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="m3 15 5-4 4 3.5 3-2.5 6 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const TEMPLATES = [
  {
    id: 'dark-tech',
    name: 'Dark Tech',
    preview: { bg: '#0d0d0d', accent: '#00e5ff', text: '#ffffff' },
    desc: 'Terminal style, neon glow, monospace font',
    prompt: `Apply the "Dark Tech" color and font theme. Change ONLY colors, fonts, backgrounds, borders, button styles, shadows, and gradients — do NOT change the page structure, section order, layout, or any HTML elements. New palette: page background #0d0d0d, surface/cards #0a0a0a, accent #00e5ff, text #ffffff. Replace the Google Fonts import with: Space Grotesk (headings, weight 700/800/900) and JetBrains Mono (body/labels, weight 400/500). Update all headings to Space Grotesk, all body text/labels to JetBrains Mono. Add a cyan text-shadow glow to the hero h1: text-shadow: 0 0 30px #00e5ff66. Accent color #00e5ff everywhere: borders, pill badges, card top-borders, button backgrounds, focus rings, icon containers. Navbar background: rgba(13,13,13,0.92). Card borders: 1px solid #00e5ff22. Inputs: background #111, border 1px solid #333, focus border-color #00e5ff. Submit button: background #00e5ff, color #000. Keep every section, the hero split layout, the typewriter script, id="hero-illustration", all section ids, all content, and the form action completely unchanged.`,
  },
  {
    id: 'corporate-blue',
    name: 'Corporate Blue',
    preview: { bg: '#1e3a5f', accent: '#f0a500', text: '#ffffff' },
    desc: 'Split layout, gold badges, professional serif',
    prompt: `Apply the "Corporate Blue" color and font theme. Change ONLY colors, fonts, backgrounds, borders, button styles, shadows, and gradients — do NOT change the page structure, section order, layout, or any HTML elements. New palette: page background #1e3a5f, surface/cards #244976, accent #f0a500, text #ffffff. Replace the Google Fonts import with: Playfair Display (headings, weight 700/800) and Inter (body, weight 400/500/600). Update all headings to Playfair Display italic, all body text to Inter. Accent color #f0a500 everywhere: pill badges, card top-borders, CTA buttons, focus rings, icon containers, eyebrow labels. Navbar background: rgba(30,58,95,0.95). Card backgrounds: #244976, border: 1px solid rgba(240,165,0,0.2). Inputs: background rgba(255,255,255,0.06), border 1px solid rgba(255,255,255,0.15), focus border-color #f0a500. Submit button: background #f0a500, color #1e3a5f. Add smooth fade-in-up animations on scroll using CSS @keyframes. Keep every section, the hero split layout, the typewriter script, id="hero-illustration", all section ids, all content, and the form action completely unchanged.`,
  },
  {
    id: 'minimal-white',
    name: 'Minimal White',
    preview: { bg: '#fafafa', accent: '#6c63ff', text: '#1a1a1a' },
    desc: 'Giant typography, lots of whitespace, editorial',
    prompt: `Apply the "Minimal White" color and font theme. Change ONLY colors, fonts, backgrounds, borders, button styles, shadows, and gradients — do NOT change the page structure, section order, layout, or any HTML elements. New palette: page background #fafafa, surface/cards #f0f0f0, accent #6c63ff, text #1a1a1a. Replace the Google Fonts import with: DM Serif Display (headings, weight 400/700) and DM Sans (body, weight 400/500/600). Update all headings to DM Serif Display, all body text to DM Sans. Accent color #6c63ff everywhere: pill badges, card top-borders, CTA buttons, focus rings, icon containers, eyebrow labels. Navbar background: rgba(250,250,250,0.95), border-bottom: 1px solid #e5e5e5. Card backgrounds: #ffffff, border: 1px solid #e8e8e8, box-shadow: 0 2px 12px rgba(0,0,0,0.06). Body text #1a1a1a, muted text #666666. Inputs: background #ffffff, border 1px solid #ddd, focus border-color #6c63ff. Submit button: background #6c63ff, color #ffffff. Keep every section, the hero split layout, the typewriter script, id="hero-illustration", all section ids, all content, and the form action completely unchanged.`,
  },
  {
    id: 'bold-green',
    name: 'Bold Green',
    preview: { bg: '#0a1f0f', accent: '#c6ff3e', text: '#ffffff' },
    desc: 'Diagonal sections, lime on black, kinetic energy',
    prompt: `Apply the "Bold Green" color and font theme. Change ONLY colors, fonts, backgrounds, borders, button styles, shadows, and gradients — do NOT change the page structure, section order, layout, or any HTML elements. New palette: page background #0a1f0f, surface/cards #0f2d1a, accent #c6ff3e, text #ffffff. Replace the Google Fonts import with: Space Grotesk (headings, weight 700/800/900) and Outfit (body, weight 400/500/600). Update all headings to Space Grotesk, all body text to Outfit. Accent color #c6ff3e everywhere: pill badges, card top-borders, CTA buttons, focus rings, icon containers, eyebrow labels. Navbar background: rgba(10,31,15,0.92). Card backgrounds: #0f2d1a, border: 1px solid rgba(198,255,62,0.15). Muted text: rgba(255,255,255,0.65). Inputs: background rgba(255,255,255,0.04), border 1px solid rgba(255,255,255,0.1), focus border-color #c6ff3e. Submit button: background #c6ff3e, color #0a1f0f. Add a subtle shimmer animation to the hero background using CSS. Keep every section, the hero split layout, the typewriter script, id="hero-illustration", all section ids, all content, and the form action completely unchanged.`,
  },
  {
    id: 'warm-orange',
    name: 'Warm & Bold',
    preview: { bg: '#1c1008', accent: '#ff6b35', text: '#ffffff' },
    desc: 'Warm tones, orange accents, creative agency',
    prompt: `Apply the "Warm & Bold" color and font theme. Change ONLY colors, fonts, backgrounds, borders, button styles, shadows, and gradients — do NOT change the page structure, section order, layout, or any HTML elements. New palette: page background #1c1008, surface/cards #2a1f14, accent #ff6b35, text #ffffff. Replace the Google Fonts import with: Syne (headings, weight 700/800) and Nunito (body, weight 400/600/700). Update all headings to Syne, all body text to Nunito. Accent color #ff6b35 everywhere: pill badges, card top-borders, CTA buttons, focus rings, icon containers, eyebrow labels. Apply a gradient to the hero h1: background: linear-gradient(135deg,#ff6b35,#ffd166); -webkit-background-clip:text; -webkit-text-fill-color:transparent. Navbar background: rgba(28,16,8,0.92). Card backgrounds: #2a1f14, border: 1px solid rgba(255,107,53,0.18). Muted text: rgba(255,255,255,0.65). Inputs: background rgba(255,255,255,0.05), border 1px solid rgba(255,255,255,0.1), focus border-color #ff6b35. Submit button: background #ff6b35, color #1c1008. Keep every section, the hero split layout, the typewriter script, id="hero-illustration", all section ids, all content, and the form action completely unchanged.`,
  },
];

export default function EditorPage() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useT();

  // Editor is admin-only — redirect regular users to CRM
  const isAdmin = Boolean(localStorage.getItem('adminToken'));
  // Set the flag synchronously (in render, before effects) so all API calls
  // immediately use the adminToken — avoids a race with the loadEditorData effect.
  if (isAdmin) {
    sessionStorage.setItem('adminEditing', 'true');
  }
  useEffect(() => {
    if (!isAdmin) {
      sessionStorage.removeItem('adminEditing');
      navigate('/crm', { replace: true });
    }
    return () => {
      sessionStorage.removeItem('adminEditing');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const chatEndRef = useRef(null);
  const uploadInputRef = useRef(null);
  const bgUploadRef = useRef(null);
  const iframeRef = useRef(null);

  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [provider] = useState(process.env.REACT_APP_LLM_PROVIDER || 'openai');
  const [refreshKey, setRefreshKey] = useState(Date.now().toString());
  const chatStorageKey = `editor_chat_${pageId}`;
  const [chat, setChat] = useState(() => {
    try {
      const saved = localStorage.getItem(`editor_chat_${pageId}`);
      if (saved) return JSON.parse(saved);
    } catch (_) {}
    return [{ role: 'assistant', content: null }];
  });
  const [error, setError] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [copied, setCopied] = useState(false);
  const [overrideDesignLock, setOverrideDesignLock] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);
  const [brand, setBrand] = useState({
    brandLogoUrl: '',
    primaryColor: '#0f1a12',
    secondaryColor: '#173126',
    accentColor: '#c6ff3e',
    designLocked: false,
  });
  const [roleDescription, setRoleDescription] = useState('');
  const [assets, setAssets] = useState([]);
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const [aiImagePrompt, setAiImagePrompt] = useState('');
  const [generatingAiImage, setGeneratingAiImage] = useState(false);
  const [generatingSuggestion, setGeneratingSuggestion] = useState(null);
  const [activeTool, setActiveTool] = useState(null);
  const [applyingTemplate, setApplyingTemplate] = useState(null);
  const [applyingBg, setApplyingBg] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  // Persist chat history to localStorage on every change
  useEffect(() => {
    try { localStorage.setItem(chatStorageKey, JSON.stringify(chat)); } catch (_) {}
  }, [chat, chatStorageKey]);

  // Tracks the language the current page HTML is stored in so we only translate on actual changes.
  // Initialised to a sentinel so the first real lang value always triggers a check.
  const pageLangRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat, busy]);

  // Auto-translate the live page whenever the selected language differs from what the page is in.
  // Uses a ref for the current pageId/provider so the closure is never stale.
  const pageIdRef = useRef(pageId);
  const providerRef = useRef(provider);
  const langRef = useRef(lang);
  pageIdRef.current = pageId;
  providerRef.current = provider;
  langRef.current = lang;

  useEffect(() => {
    // Skip on first mount — wait until we know the page has loaded (handled in loadEditorData).
    if (pageLangRef.current === null) return;
    // Nothing to do if the page is already in the right language.
    if (pageLangRef.current === lang) return;

    pageLangRef.current = lang;
    const langName = lang === 'de' ? 'German' : 'English';

    // Immediately update footer language in iframe (before translation completes)
    try {
      iframeRef.current?.contentWindow?.postMessage({ type: 'langChange', lang }, '*');
    } catch (_) {}

    setBusy(true);
    setError('');
    setChat((prev) => [...prev, { role: 'user', content: `🌐 Switching page language to ${langName}…` }]);

    translatePage({ pageId: pageIdRef.current, lang })
      .then((result) => {
        setChat((prev) => [...prev, { role: 'assistant', content: result.assistantMessage }]);
        setRefreshKey(Date.now().toString());
      })
      .catch((err) => setError(err.message))
      .finally(() => setBusy(false));
  // lang is the only real trigger; refs keep the closure fresh without re-subscribing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const liveUrl = useMemo(() => getLivePageUrl(pageId, refreshKey), [pageId, refreshKey]);
  const stableUrl = useMemo(() => getLivePageUrl(pageId), [pageId]);

  useEffect(() => {
    async function loadEditorData() {
      try {
        const [pageData, assetsData] = await Promise.all([
          getPageDetails(pageId),
          listPageAssets(pageId),
        ]);

        setBrand({
          brandLogoUrl: pageData.brandLogoUrl || '',
          primaryColor: pageData.primaryColor || '#0f1a12',
          secondaryColor: pageData.secondaryColor || '#173126',
          accentColor: pageData.accentColor || '#c6ff3e',
          designLocked: Boolean(pageData.designLocked),
        });
        setRoleDescription(pageData.description || '');
        setAssets(assetsData.assets || []);

        // Record the page's actual stored language (from DB).
        // If it differs from the current UI lang the lang-switch effect will
        // fire and auto-translate — e.g. page generated in EN while admin is in DE.
        pageLangRef.current = pageData.htmlLang || 'en';
      } catch (err) {
        setError(err.message);
      }
    }

    loadEditorData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  async function onSend(e) {
    e.preventDefault();
    if (!message.trim()) return;

    const userMsg = { role: 'user', content: message.trim() };
    const nextChat = [...chat, userMsg];
    setChat(nextChat);
    setMessage('');
    setError('');
    setBusy(true);

    try {
      const result = await editPage({
        pageId,
        message: userMsg.content,
        history: nextChat,
        provider,
        overrideDesignLock,
        lang,
      });
      setChat((current) => [...current, { role: 'assistant', content: result.assistantMessage }]);
      setRefreshKey(Date.now().toString());
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      setOverrideDesignLock(false);
    }
  }

  async function onSaveBrand(e) {
    e.preventDefault();
    setError('');
    setSavingBrand(true);
    try {
      await updatePageBrand(pageId, {
        brandLogoUrl: brand.brandLogoUrl || null,
        primaryColor: brand.primaryColor,
        secondaryColor: brand.secondaryColor,
        accentColor: brand.accentColor,
        designLocked: Boolean(brand.designLocked),
      });

      const brandUpdateInstruction = [
        'Update the current page styling to match this brand kit without changing core copy/content structure.',
        brand.brandLogoUrl ? `Use this brand logo URL prominently: ${brand.brandLogoUrl}.` : null,
        `Primary color: ${brand.primaryColor}.`,
        `Secondary color: ${brand.secondaryColor}.`,
        `Accent color: ${brand.accentColor}.`,
        'Keep the page dynamic and responsive.',
      ]
        .filter(Boolean)
        .join(' ');

      const brandMsg = { role: 'user', content: brandUpdateInstruction };
      const nextChat = [...chat, brandMsg];
      setChat(nextChat);

      const result = await editPage({
        pageId,
        message: brandUpdateInstruction,
        history: nextChat,
        provider,
        overrideDesignLock: true,
        lang,
      });
      setChat((current) => [...current, { role: 'assistant', content: result.assistantMessage }]);
      setRefreshKey(Date.now().toString());
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingBrand(false);
    }
  }

  async function onUploadAsset(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError('');
    setUploadingAsset(true);
    try {
      const result = await uploadPageAsset({ pageId, file });
      setAssets((prev) => [result.asset, ...prev]);
      e.target.value = '';
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploadingAsset(false);
    }
  }

  function openUploadPicker() {
    uploadInputRef.current?.click();
  }

  async function onDeleteAsset(assetId) {
    setUploadError('');
    try {
      await deletePageAsset(assetId);
      setAssets((prev) => prev.filter((item) => item.id !== assetId));
    } catch (err) {
      setUploadError(err.message);
    }
  }

  async function onGenerateAiImage(e) {
    e.preventDefault();
    if (!aiImagePrompt.trim()) return;

    setUploadError('');
    setGeneratingAiImage(true);
    try {
      const result = await generatePageAsset({ pageId, prompt: aiImagePrompt.trim() });
      setAssets((prev) => [result.asset, ...prev]);
      setAiImagePrompt('');
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setGeneratingAiImage(false);
    }
  }

  async function onGenerateSuggestion(index, prompt) {
    setUploadError('');
    setGeneratingSuggestion(index);
    try {
      const result = await generatePageAsset({ pageId, prompt });
      setAssets((prev) => [result.asset, ...prev]);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setGeneratingSuggestion(null);
    }
  }

  async function onApplyTemplate(template) {
    setApplyingTemplate(template.id);
    setActiveTool(null);
    const userMsg = { role: 'user', content: `Apply the "${template.name}" design template.` };
    const nextChat = [...chat, userMsg];
    setChat(nextChat);
    setError('');
    setBusy(true);
    try {
      const result = await editPage({
        pageId,
        message: template.prompt,
        history: nextChat,
        provider,
        overrideDesignLock: true,
        lang,
      });
      setChat((current) => [...current, { role: 'assistant', content: result.assistantMessage }]);
      setRefreshKey(Date.now().toString());
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      setApplyingTemplate(null);
    }
  }

  async function applyBgUrl(url) {
    setApplyingBg(true);
    setActiveTool(null);
    const instruction = `Set a full-page background image using this URL: "${url}". Apply it to the body element: background-image: url('${url}'); background-size: cover; background-position: center; background-attachment: fixed; background-repeat: no-repeat. Add a semi-transparent dark overlay as the first child of body (position:fixed; top:0; left:0; width:100%; height:100%; background:rgba(0,0,0,0.55); z-index:0; pointer-events:none). Set all direct section/div children to position:relative; z-index:1 so content appears above the overlay. Keep all existing content, styles and form action unchanged.`;
    const userMsg = { role: 'user', content: 'Set full-page background image.' };
    const nextChat = [...chat, userMsg];
    setChat(nextChat);
    setError('');
    setBusy(true);
    try {
      const result = await editPage({ pageId, message: instruction, history: nextChat, provider, overrideDesignLock: true, lang });
      setChat((current) => [...current, { role: 'assistant', content: result.assistantMessage }]);
      setRefreshKey(Date.now().toString());
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      setApplyingBg(false);
    }
  }

  async function onBgFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBg(true);
    setUploadError('');
    try {
      const result = await uploadPageAsset({ pageId, file });
      setAssets((prev) => [result.asset, ...prev]);
      const url = `${API_BASE}${result.asset.public_url}`;
      e.target.value = '';
      await applyBgUrl(url);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploadingBg(false);
    }
  }


  const imageSuggestions = useMemo(() => {
    const desc = (roleDescription || '').toLowerCase();

    const isIT = /software|developer|engineer.*it|coding|it |tech|programm|entwickler|devops|frontend|backend|fullstack|data.?scien|cloud|cyber|network|architect|java|python|react|node/i.test(desc);
    const isMedical = /doctor|arzt|ärztin|nurse|pflege|krankenhaus|hospital|medizin|medical|health|patient|klinik|clinic|pharma|zahnarzt|dentist/i.test(desc);
    const isSales = /sales|vertrieb|verkauf|account.?manag|kundenbetreuung|außendienst|key.?account/i.test(desc);
    const isMarketing = /marketing|brand|seo|content|social.?media|kampagne|campaign|werbung/i.test(desc);
    const isFinance = /financ|finanz|buchhalt|accounting|steuer|bank|investment|treasury|controller|auditor|rechnungs/i.test(desc);
    const isLegal = /\blaw\b|legal|recht|jurist|anwalt|compliance|contract|vertrag|notar/i.test(desc);
    const isEngineering = /mechanical|maschinenbau|elektro|electrical|civil|manufacturing|produktion|ingenieur|quality|qualität|process.?engineer|fertigungs/i.test(desc);
    const isHR = /\bhr\b|human.?resource|personalwesen|recruiter|recruitment|talent.?acqui|personalvermittl|bewerbung/i.test(desc);
    const isEducation = /teacher|lehrer|schule|school|trainer|ausbildung|coach|pedagog|dozent|lecturer/i.test(desc);
    const isLogistics = /logistics|logistik|supply.?chain|warehouse|lager|transport|spedition|fahrer|driver|fleet/i.test(desc);
    const isDesign = /\bdesigner\b|graphic|grafik|ux\b|ui\b|creative|art.?director|motion/i.test(desc);
    const isConstruction = /construction|bau\b|architect|bauleiter|site.?manag|handwerk|carpenter|electric.*trade/i.test(desc);
    const isHospitality = /hotel|restaurant|gastro|chef|cook|waiter|kellner|küche|hospitality|tourism|tourismus/i.test(desc);

    let roleLabel, env, tools, activity1, activity2;

    if (isIT) {
      roleLabel = 'software developer';
      env = 'modern tech office with multiple monitors showing code';
      tools = 'laptop, multiple monitors with IDE and code, keyboard';
      activity1 = 'focused software developer coding at workstation with multiple screens showing programming code';
      activity2 = 'software engineering team doing code review, pair programming session around monitors';
    } else if (isMedical) {
      roleLabel = 'medical professional';
      env = 'modern German hospital or bright medical clinic';
      tools = 'stethoscope, medical equipment, clinical attire';
      activity1 = 'professional doctor or nurse in a bright modern clinic during patient consultation';
      activity2 = 'medical team collaborating in hospital, reviewing patient data on screens';
    } else if (isSales) {
      roleLabel = 'sales professional';
      env = 'modern open-plan office or meeting room';
      tools = 'laptop, sales presentation on screen, phone';
      activity1 = 'confident sales professional presenting to clients in a bright meeting room';
      activity2 = 'sales team reviewing performance charts on a large screen, energetic discussion';
    } else if (isMarketing) {
      roleLabel = 'marketing professional';
      env = 'creative open-plan office with campaign materials';
      tools = 'laptop, large display with campaign visuals, whiteboard';
      activity1 = 'marketing specialist working on creative campaign with laptop and design screens';
      activity2 = 'marketing team brainstorming campaign ideas on whiteboard in bright office';
    } else if (isFinance) {
      roleLabel = 'finance professional';
      env = 'professional corporate office with financial data screens';
      tools = 'financial charts on monitors, documents, laptop';
      activity1 = 'finance analyst reviewing data dashboards and financial charts at a professional desk';
      activity2 = 'finance team in meeting room analyzing quarterly reports on large screen';
    } else if (isLegal) {
      roleLabel = 'legal professional';
      env = 'professional law office with bookshelves and a clean modern desk';
      tools = 'legal documents, contracts, laptop';
      activity1 = 'professional lawyer carefully reviewing legal documents at a clean executive desk';
      activity2 = 'legal team in structured discussion around conference table with documents';
    } else if (isEngineering) {
      roleLabel = 'mechanical or industrial engineer';
      env = 'modern German engineering facility or production floor';
      tools = 'technical blueprints, safety helmet, precision instruments';
      activity1 = 'engineer examining technical blueprints or CAD diagrams at a workstation';
      activity2 = 'engineering team inspecting machinery on a clean modern production floor with safety gear';
    } else if (isHR) {
      roleLabel = 'HR professional';
      env = 'welcoming modern office with a friendly atmosphere';
      tools = 'laptop, documents, meeting room with plants';
      activity1 = 'friendly HR professional conducting a job interview in a modern bright office';
      activity2 = 'HR team reviewing candidate profiles together in a collaborative meeting';
    } else if (isEducation) {
      roleLabel = 'professional trainer or educator';
      env = 'modern training room or classroom with projector';
      tools = 'whiteboard, training materials, projector';
      activity1 = 'confident trainer presenting to a small engaged group in a bright modern training room';
      activity2 = 'collaborative workshop session with participants actively learning and taking notes';
    } else if (isLogistics) {
      roleLabel = 'logistics coordinator';
      env = 'modern logistics center or operations office';
      tools = 'tablet, warehouse environment, operations screens';
      activity1 = 'logistics professional coordinating operations in a modern well-organized warehouse';
      activity2 = 'logistics team reviewing delivery schedules and supply chain data on screens';
    } else if (isDesign) {
      roleLabel = 'creative designer';
      env = 'bright creative studio with design tools and large monitors';
      tools = 'drawing tablet, large monitor with design software, color samples';
      activity1 = 'designer working on visual project with drawing tablet and large monitor showing design software';
      activity2 = 'creative team reviewing design mockups on large screen in a bright studio';
    } else if (isConstruction) {
      roleLabel = 'construction or site professional';
      env = 'modern construction site or architectural office';
      tools = 'safety helmet, blueprints, tablet';
      activity1 = 'site manager reviewing building plans on tablet at a construction site with safety gear';
      activity2 = 'construction project team meeting with architectural blueprints spread on a table';
    } else if (isHospitality) {
      roleLabel = 'hospitality professional';
      env = 'modern upscale German hotel or restaurant';
      tools = 'professional hospitality attire, elegant surroundings';
      activity1 = 'professional hotel or restaurant staff welcoming guests with a warm smile';
      activity2 = 'hospitality team briefing in an elegant hotel lobby or restaurant setting';
    } else {
      roleLabel = 'professional employee';
      env = 'modern German office';
      tools = 'laptop, office equipment';
      activity1 = 'professional employee focused at a clean modern office workstation';
      activity2 = 'diverse professional team collaborating in a bright contemporary office';
    }

    const style = 'professional photograph, Getty Images style, DSLR 85mm lens, natural daylight, sharp focus — no color filters, no neon effects, no tinting, no digital overlays, no color grading, accurate natural colors, NOT CGI, NOT illustration';
    const context = 'contemporary German workplace in Germany, Central European professional appearance, natural accurate skin tones';

    return [
      {
        label: 'Employee Portrait',
        icon: '👤',
        desc: `Individual ${roleLabel}`,
        prompt: `Professional portrait photograph of a Central European ${roleLabel} in ${env}. The person has typical German/Central European appearance, confident and approachable expression, wearing professional work attire. Clear face, no overlays. ${style}. ${context}.`,
      },
      {
        label: 'Role in Action',
        icon: '💼',
        desc: `${roleLabel} at work`,
        prompt: `Candid action photograph: ${activity1}. Visible tools: ${tools}. Person is fully engaged in their task, not looking at camera. ${style}. ${context}.`,
      },
      {
        label: 'Team Collaboration',
        icon: '👥',
        desc: 'Team working together',
        prompt: `Candid team photograph: ${activity2}. 3-4 real colleagues, natural interaction, genuine engagement — not staged poses. ${style}. ${context}.`,
      },
      {
        label: 'Workplace Scene',
        icon: '🏢',
        desc: 'Work environment',
        prompt: `Wide-angle workplace photograph of ${env}. Real employees of ${roleLabel} background visible in the frame, naturally going about their work. Bright, welcoming atmosphere. ${style}. ${context}.`,
      },
      {
        label: 'Success Moment',
        icon: '🎯',
        desc: 'Achievement & motivation',
        prompt: `Authentic candid photograph: a ${roleLabel} and colleague sharing a genuine positive moment — reviewing results, a handshake, or quiet satisfaction at completing a task. Real emotion, unposed. ${style}. ${context}.`,
      },
    ];
  }, [roleDescription]);

  function onInsertAssetUrl(asset) {
    const fullUrl = `${API_BASE}${asset.public_url}`;
    setMessage((prev) => {
      const prefix = prev.trim().length > 0 ? `${prev.trim()} ` : '';
      return `${prefix}Use this uploaded ${asset.asset_type || 'media'} URL in the page: ${fullUrl}`;
    });
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(stableUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setError('Clipboard unavailable - copy the URL from the address bar.');
    }
  }

  return (
    <main className="editor-page-wrap">
      <div className="grain" />

      <AppNav
        activePage="editor"
        showLinks={!isAdmin}
        rightSlot={(
          <>
            {isAdmin && (
              <button className="appnav-link" type="button" onClick={() => { sessionStorage.removeItem('adminEditing'); navigate('/admin'); }}>
                ← Back to Admin
              </button>
            )}
            <button className="appnav-logout-btn" type="button" onClick={copyLink} style={{ borderColor: 'rgba(198,255,62,0.4)', color: '#c6ff3e' }}>
              {copied ? t('navCopied') : t('navCopyLink')}
            </button>
          </>
        )}
      />

      <div className="editor-shell">
        <section className="preview-panel">
          <div className="ed-eyebrow">
            <span className="dot-live" /> {t('editorLivePreview')}
          </div>

          <iframe
            ref={iframeRef}
            title="Live generated page"
            className="preview-frame"
            src={liveUrl}
          />
        </section>

        <section className="chat-panel">
          <div className="ed-eyebrow">
            <span className="dot-edit" /> {t('editorDiffView')}
          </div>

          <div className="chat-log">
            {chat.map((entry, i) => (
              <div
                key={`${entry.role}-${i}`}
                className={`chat-bubble ${entry.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'}`}
              >
                <div className="chat-bubble-avatar">
                  {entry.role === 'user' ? 'U' : 'AI'}
                </div>
                <div className="chat-bubble-body">
                  <span className="chat-bubble-role">
                    {entry.role === 'user' ? 'You' : 'AI'}
                  </span>
                  <p className="chat-bubble-text">
                    {entry.content || t('editorInitialMsg')}
                  </p>
                </div>
              </div>
            ))}
            {busy && (
              <div className="chat-bubble chat-bubble-ai">
                <div className="chat-bubble-avatar">AI</div>
                <div className="chat-bubble-body">
                  <span className="chat-bubble-role">AI</span>
                  <div className="chat-typing-dots">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="chat-input-bar">
            {activeTool === 'media' && (
              <div className="chat-tool-panel chat-tool-panel-media chat-tool-panel-float chat-tool-panel-float-left" style={{ display:'flex', flexDirection:'column', maxHeight:'52vh' }}>
                {/* scrollable content */}
                <div style={{ overflowY:'auto', flex:1, display:'flex', flexDirection:'column', gap:'0.55rem' }}>
                  <div className="chat-tool-actions">
                    <button className="chat-tool-action" type="button" onClick={openUploadPicker} disabled={uploadingAsset}>
                      {uploadingAsset ? t('editorUploading') : t('editorUploadImage')}
                    </button>
                    <input
                      ref={uploadInputRef}
                      id="editorAssetUpload"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml,image/bmp,image/tiff,image/heic,image/heif,image/avif,.jpg,.jpeg,.png,.gif,.webp,.svg,.bmp,.tiff,.tif,.heic,.heif,.avif,video/*"
                      onChange={onUploadAsset}
                      disabled={uploadingAsset}
                      style={{ display: 'none' }}
                    />
                    <form className="chat-tool-inline-form" onSubmit={onGenerateAiImage}>
                      <input
                        className="diff-inp"
                        placeholder={t('editorAiImagePlaceholder')}
                        value={aiImagePrompt}
                        onChange={(e) => setAiImagePrompt(e.target.value)}
                        disabled={generatingAiImage}
                      />
                      <button className="diff-send" type="submit" disabled={generatingAiImage || !aiImagePrompt.trim()}>
                        {generatingAiImage ? '...' : t('editorGenerate')}
                      </button>
                    </form>
                  </div>

                  <div className="img-suggestions">
                    <p className="img-suggestions-title">Image Suggestions</p>
                    <div className="img-suggestions-grid">
                      {imageSuggestions.map((s, i) => (
                        <div key={i} className="img-suggestion-card">
                          <div className="img-suggestion-icon">{s.icon}</div>
                          <div className="img-suggestion-info">
                            <strong>{s.label}</strong>
                            <span>{s.desc}</span>
                          </div>
                          <button
                            type="button"
                            className="img-suggestion-btn"
                            disabled={generatingSuggestion !== null}
                            onClick={() => onGenerateSuggestion(i, s.prompt)}
                          >
                            {generatingSuggestion === i ? '...' : 'Generate'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <ul className="editor-asset-list editor-asset-list-compact">
                    {assets.map((asset) => (
                      <li key={asset.id}>
                        <a href={`${API_BASE}${asset.public_url}`} target="_blank" rel="noreferrer">
                          {asset.original_name || asset.public_url}
                        </a>
                        <div className="editor-asset-actions">
                          <button type="button" onClick={() => onInsertAssetUrl(asset)}>{t('editorUse')}</button>
                          <button type="button" onClick={() => onDeleteAsset(asset.id)}>{t('editorRemove')}</button>
                        </div>
                      </li>
                    ))}
                    {assets.length === 0 && <li className="editor-empty-row">{t('editorNoAssets')}</li>}
                  </ul>
                  {uploadError && <p className="auth-error">{uploadError}</p>}
                </div>

                {/* close always pinned at bottom */}
                <button className="chat-tool-close" type="button" onClick={() => setActiveTool(null)} style={{ width:'100%', marginTop:'6px', flexShrink:0 }}>
                  ✕ {t('editorClose')}
                </button>
              </div>
            )}

            {activeTool === 'colors' && (
              <div className="chat-tool-panel chat-tool-panel-colors chat-tool-panel-float chat-tool-panel-float-right">
                <form className="editor-brand-form" onSubmit={onSaveBrand}>
                  <label htmlFor="editorBrandLogo">{t('editorBrandLogoLabel')}</label>
                  <input
                    id="editorBrandLogo"
                    className="diff-inp"
                    value={brand.brandLogoUrl}
                    onChange={(e) => setBrand((prev) => ({ ...prev, brandLogoUrl: e.target.value }))}
                    placeholder={t('editorBrandLogoPlaceholder')}
                  />

                  <div className="editor-color-row">
                    <label htmlFor="editorPrimaryColor">{t('editorPrimary')}</label>
                    <input
                      id="editorPrimaryColor"
                      type="color"
                      value={brand.primaryColor}
                      onChange={(e) => setBrand((prev) => ({ ...prev, primaryColor: e.target.value }))}
                    />
                    <label htmlFor="editorSecondaryColor">{t('editorSecondary')}</label>
                    <input
                      id="editorSecondaryColor"
                      type="color"
                      value={brand.secondaryColor}
                      onChange={(e) => setBrand((prev) => ({ ...prev, secondaryColor: e.target.value }))}
                    />
                    <label htmlFor="editorAccentColor">{t('editorAccent')}</label>
                    <input
                      id="editorAccentColor"
                      type="color"
                      value={brand.accentColor}
                      onChange={(e) => setBrand((prev) => ({ ...prev, accentColor: e.target.value }))}
                    />
                  </div>

                  <label className="editor-checkbox-row" htmlFor="editorDesignLock">
                    <input
                      id="editorDesignLock"
                      type="checkbox"
                      checked={brand.designLocked}
                      onChange={(e) => setBrand((prev) => ({ ...prev, designLocked: e.target.checked }))}
                    />
                    {t('editorKeepBrandLock')}
                  </label>

                  <button className="diff-send" type="submit" disabled={savingBrand}>
                    {savingBrand ? t('editorSavingBrand') : t('editorSaveBrand')}
                  </button>
                </form>
                <button className="chat-tool-close" type="button" onClick={() => setActiveTool(null)} style={{ width: '100%', marginTop: '4px' }}>
                  ✕ {t('editorClose')}
                </button>
              </div>
            )}

            {activeTool === 'templates' && (
              <div className="chat-tool-panel chat-tool-panel-float chat-tool-panel-float-left" style={{ width: '340px' }}>
                <div className="template-grid">
                  {TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.id}
                      className={`template-card${applyingTemplate === tpl.id ? ' template-card-loading' : ''}`}
                      type="button"
                      disabled={busy || applyingTemplate !== null}
                      onClick={() => onApplyTemplate(tpl)}
                    >
                      <div className="template-preview" style={{ background: tpl.preview.bg }}>
                        <div className="template-preview-bar" style={{ background: tpl.preview.accent }} />
                        <div className="template-preview-lines">
                          <div style={{ background: tpl.preview.text, opacity: 0.9, width: '70%', height: '6px', borderRadius: '3px', marginBottom: '5px' }} />
                          <div style={{ background: tpl.preview.text, opacity: 0.5, width: '90%', height: '4px', borderRadius: '3px', marginBottom: '4px' }} />
                          <div style={{ background: tpl.preview.text, opacity: 0.5, width: '60%', height: '4px', borderRadius: '3px', marginBottom: '8px' }} />
                          <div style={{ background: tpl.preview.accent, width: '40%', height: '14px', borderRadius: '4px' }} />
                        </div>
                      </div>
                      <div className="template-info">
                        <strong>{tpl.name}</strong>
                        <span>{tpl.desc}</span>
                      </div>
                      {applyingTemplate === tpl.id && <div className="template-spinner">Applying…</div>}
                    </button>
                  ))}
                </div>
                <button className="chat-tool-close" type="button" onClick={() => setActiveTool(null)} style={{ width: '100%', marginTop: '8px' }}>
                  ✕ Close
                </button>
              </div>
            )}

            {activeTool === 'background' && (
              <div className="chat-tool-panel chat-tool-panel-float chat-tool-panel-float-left" style={{ width: '320px' }}>
                <input
                  ref={bgUploadRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml,image/bmp,image/tiff,image/heic,image/heif,image/avif,.jpg,.jpeg,.png,.gif,.webp,.svg,.bmp,.tiff,.tif,.heic,.heif,.avif"
                  style={{ display: 'none' }}
                  onChange={onBgFileChange}
                  disabled={uploadingBg || applyingBg}
                />
                <button
                  type="button"
                  className="diff-send"
                  style={{ width: '100%', marginBottom: '12px' }}
                  disabled={uploadingBg || applyingBg || busy}
                  onClick={() => bgUploadRef.current?.click()}
                >
                  {uploadingBg ? 'Uploading…' : applyingBg ? 'Applying…' : '↑ Upload & Apply Image'}
                </button>

                {assets.filter(a => a.asset_type === 'image').length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <p style={{ fontSize: '11px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Or use already uploaded image</p>
                    {assets.filter(a => a.asset_type === 'image').slice(0, 5).map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        className="img-suggestion-card"
                        disabled={applyingBg || busy}
                        onClick={() => applyBgUrl(`${API_BASE}${a.public_url}`)}
                      >
                        <img src={`${API_BASE}${a.public_url}`} alt="" style={{ width: '48px', height: '36px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
                        <span style={{ fontSize: '12px', color: '#ccc', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.original_name || 'image'}</span>
                      </button>
                    ))}
                  </div>
                )}
                <button className="chat-tool-close" type="button" onClick={() => setActiveTool(null)} style={{ width: '100%', marginTop: '8px' }}>
                  ✕ Close
                </button>
              </div>
            )}

            <div className="chat-input-toolbar">
              <div className="chat-input-tool-group">
                <div className="chat-input-tool-group-wrap">
                  <button
                    className={`chat-tool-btn${activeTool === 'media' ? ' active' : ''}`}
                    type="button"
                    onClick={() => setActiveTool((prev) => (prev === 'media' ? null : 'media'))}
                    aria-label="Media tools"
                    aria-expanded={activeTool === 'media'}
                  >
                    <ToolIconMedia />
                  </button>
                  <button
                    className={`chat-tool-btn${activeTool === 'colors' ? ' active' : ''}`}
                    type="button"
                    onClick={() => setActiveTool((prev) => (prev === 'colors' ? null : 'colors'))}
                    aria-label="Brand colors"
                    aria-expanded={activeTool === 'colors'}
                  >
                    <ToolIconColors />
                  </button>
                  <button
                    className={`chat-tool-btn${activeTool === 'templates' ? ' active' : ''}`}
                    type="button"
                    onClick={() => setActiveTool((prev) => (prev === 'templates' ? null : 'templates'))}
                    aria-label="Design templates"
                    aria-expanded={activeTool === 'templates'}
                    title="Switch design template"
                  >
                    <ToolIconTemplates />
                  </button>
                  <button
                    className={`chat-tool-btn${activeTool === 'background' ? ' active' : ''}`}
                    type="button"
                    onClick={() => setActiveTool((prev) => (prev === 'background' ? null : 'background'))}
                    aria-label="Background image"
                    title="Set full-page background image"
                  >
                    <ToolIconBackground />
                  </button>
                </div>

              </div>

              <div className="chat-input-meta">
                <label className="editor-checkbox-row" htmlFor="overrideDesignLock">
                  <input
                    id="overrideDesignLock"
                    type="checkbox"
                    checked={overrideDesignLock}
                    onChange={(e) => setOverrideDesignLock(e.target.checked)}
                  />
                  {t('editorOverrideDesignLock')}
                </label>
              </div>
            </div>

            <form className="chat-prompt-form" onSubmit={onSend}>
              <div className="chat-input-row">
                <textarea
                  className="chat-inp"
                  placeholder={t('editorPlaceholder')}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onSend(e);
                    }
                  }}
                  rows={1}
                />
                <button className="chat-send-btn" type="submit" disabled={busy}>
                  {busy ? '...' : '->'}
                </button>
              </div>
            </form>

          </div>

          {error && <p className="auth-error">{error}</p>}
        </section>
      </div>
    </main>
  );
}
